import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { EmailSetupWizard } from '../components/EmailSetupWizard';

interface EmailAccount {
  id: string;
  provider: string;
  authMethod: 'oauth_gmail' | 'app_password';
  email: string;
  imapHost: string | null;
  imapPort: number | null;
  smtpHost: string | null;
  smtpPort: number | null;
  status: 'connected' | 'disconnected' | 'error';
  lastConnectedAt: string | null;
  createdAt: string;
}

function statusBadge(status: EmailAccount['status']) {
  const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
  if (status === 'connected') return <span className={`${base} bg-green-100 text-green-800`}>Connected</span>;
  if (status === 'error') return <span className={`${base} bg-red-100 text-red-800`}>Error</span>;
  return <span className={`${base} bg-gray-100 text-gray-800`}>Disconnected</span>;
}

export default function OutreachPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);
  const [wizardTestAccount, setWizardTestAccount] = useState<{ id: string; email: string } | undefined>();
  const [testResult, setTestResult] = useState<Record<string, string> | null>(null);

  const oauthSuccess = searchParams.get('connected') === 'gmail';
  const oauthError = searchParams.get('error');

  const { data, isLoading } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => apiGet<{ emailAccounts: EmailAccount[] }>('/email-accounts'),
  });

  const accounts = data?.emailAccounts ?? [];

  // When returning from Gmail OAuth, open wizard at test-email step for the newest Gmail account
  useEffect(() => {
    if (oauthSuccess && accounts.length > 0) {
      const gmailAccount = accounts
        .filter((a) => a.authMethod === 'oauth_gmail')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

      if (gmailAccount) {
        setWizardTestAccount({ id: gmailAccount.id, email: gmailAccount.email });
        setWizardKey((k) => k + 1);
        setWizardOpen(true);
        // Remove the ?connected=gmail param so refreshing doesn't re-open the wizard
        setSearchParams({}, { replace: true });
      }
    }
  }, [oauthSuccess, accounts.length]);

  const testAccount = useMutation({
    mutationFn: (id: string) =>
      apiPost<{ ok: boolean; errors?: Record<string, string> }>(`/email-accounts/${id}/test`),
    onSuccess: (res) => {
      setTestResult(res.errors ?? { result: res.ok ? 'Connection successful' : 'Connection failed' });
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
    },
  });

  const disconnectAccount = useMutation({
    mutationFn: (id: string) => apiDelete(`/email-accounts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-accounts'] }),
  });

  function openWizard() {
    setWizardTestAccount(undefined);
    setWizardKey((k) => k + 1);
    setWizardOpen(true);
  }

  function closeWizard() {
    setWizardOpen(false);
    queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Outreach</h1>
        <p className="mt-1 text-sm text-gray-500">
          Connect your email account to send pitches directly from your inbox — no "sent via" markers.
        </p>
      </div>

      {oauthError && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800">
          Gmail connection failed: {decodeURIComponent(oauthError)}
        </div>
      )}

      {/* Connected Accounts */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">Connected accounts</h2>
          <button
            onClick={openWizard}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add account
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : accounts.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
            <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-medium text-gray-600 mb-1">No email accounts connected</p>
            <p className="text-xs text-gray-400 mb-4">Connect your Gmail, Outlook, or other email account to start sending pitches.</p>
            <button
              onClick={openWizard}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Connect an account
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {accounts.map((account) => (
              <div key={account.id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{account.email}</p>
                    {statusBadge(account.status)}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {account.provider} ·{' '}
                    {account.authMethod === 'oauth_gmail' ? 'Google OAuth' : 'App password'}
                    {account.lastConnectedAt && (
                      <> · Last connected {new Date(account.lastConnectedAt).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => testAccount.mutate(account.id)}
                    disabled={testAccount.isPending}
                    className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    Test
                  </button>
                  <button
                    onClick={() => disconnectAccount.mutate(account.id)}
                    disabled={disconnectAccount.isPending}
                    className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {testResult && (
          <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700">
            {Object.entries(testResult).map(([k, v]) => (
              <div key={k}><strong>{k}:</strong> {v}</div>
            ))}
            <button onClick={() => setTestResult(null)} className="mt-1 text-gray-400 hover:text-gray-600">Dismiss</button>
          </div>
        )}
      </section>

      <EmailSetupWizard
        key={wizardKey}
        isOpen={wizardOpen}
        onClose={closeWizard}
        startAtTestEmail={wizardTestAccount}
      />
    </div>
  );
}
